import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Music2, Clock, ArrowRight, Clipboard, Check, Trash2, Mic } from 'lucide-react';
import { AudioFile, deleteAudioFile } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useUserFiles } from '../contexts/UserFilesContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { useExpiringFiles } from '../hooks/useExpiringFiles';
import { FileRetention } from './FileRetention';
import {
  getShareableUrl,
  copyToClipboard,
  isPermanentFile
} from '../utils/fileUtils';

const RecentFiles: React.FC = () => {
  const navigate = useNavigate();
  const { getValidToken } = useAuth();
  const { files, loading, error, refresh, removeFile } = useUserFiles();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogComponent } = useConfirm();
  const [copiedFileId, setCopiedFileId] = useState<string | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [recentlyClaimedCount, setRecentlyClaimedCount] = useState(0);

  // When any file is expiring within 24h, surface the dying files first —
  // matches the trigger condition for ExpiringFilesBanner so the two surfaces
  // tell a consistent story.
  const { hasExpiringFiles } = useExpiringFiles(files);

  const displayedFiles = useMemo(() => {
    const sorted = [...files];
    if (hasExpiringFiles) {
      sorted.sort((a, b) => {
        const aPerm = isPermanentFile(a.ttl);
        const bPerm = isPermanentFile(b.ttl);
        if (aPerm && !bPerm) return 1;
        if (!aPerm && bPerm) return -1;
        if (aPerm && bPerm) return 0;
        return (a.ttl ?? 0) - (b.ttl ?? 0);
      });
    } else {
      sorted.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
    }
    return sorted.slice(0, 5);
  }, [files, hasExpiringFiles]);

  // Mark as animated once files first arrive so subsequent updates don't re-animate.
  useEffect(() => {
    if (!loading && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [loading, hasAnimated]);

  // Check for recently claimed files
  useEffect(() => {
    const claimedData = localStorage.getItem('recentlyClaimedFiles');
    console.log('[RecentFiles] Checking for claimed files:', claimedData);
    if (claimedData) {
      try {
        const { timestamp, count } = JSON.parse(claimedData);
        const age = Date.now() - timestamp;
        console.log(`[RecentFiles] Found claimed files: count=${count}, age=${age}ms`);

        // Show highlight for 10 seconds
        if (age < 10000) {
          console.log(`[RecentFiles] Setting recentlyClaimedCount to ${count}`);
          setRecentlyClaimedCount(count);
          // Clear after 10 seconds
          setTimeout(() => {
            console.log('[RecentFiles] Clearing claimed files highlight');
            setRecentlyClaimedCount(0);
            localStorage.removeItem('recentlyClaimedFiles');
          }, 10000);
        } else {
          console.log('[RecentFiles] Claimed files too old, removing');
          localStorage.removeItem('recentlyClaimedFiles');
        }
      } catch (error) {
        console.error('Failed to parse claimed files data:', error);
      }
    }
  }, [files]); // Re-check when files change

  // Poll for files with pending/processing status — refresh the shared context.
  useEffect(() => {
    const hasProcessingFiles = files.some(
      f => f.processingStatus === 'pending' || f.processingStatus === 'processing'
    );

    if (!hasProcessingFiles) {
      return;
    }

    const intervalId = setInterval(() => {
      refresh();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [files, refresh]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-500 border border-green-500/30">
            Ready
          </span>
        );
      case 'processing':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-500 border border-yellow-500/30">
            Processing
          </span>
        );
      case 'failed':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-500 border border-red-500/30">
            Failed
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-500/20 text-neutral-500 border border-neutral-500/30">
            Unknown
          </span>
        );
    }
  };

  const handleCopyLink = async (fileId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const url = getShareableUrl(fileId);
      await copyToClipboard(url);
      setCopiedFileId(fileId);
      setTimeout(() => setCopiedFileId(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleDeleteFile = async (fileId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    const confirmed = await confirm({
      title: 'Delete File',
      message: 'Are you sure you want to delete this file? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    try {
      setDeletingFileId(fileId);
      const token = await getValidToken();
      if (!token) {
        showToast('Authentication required to delete files', 'error');
        return;
      }

      await deleteAudioFile(fileId, token);

      // Remove from the shared context — QuickStats / PlaysThisWeekHero update with us.
      removeFile(fileId);

      showToast('File deleted successfully', 'success');
    } catch (err: any) {
      console.error('Failed to delete file:', err);
      showToast(err.message || 'Failed to delete file', 'error');
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleFileClick = (file: AudioFile) => {
    // Navigate to player page for all files
    navigate(`/player/${file.id}`);
  };

  return (
    <div className="rounded-2xl border p-6 shadow-sm" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Recent Uploads
        </h2>
        <button
          onClick={() => navigate('/my-files')}
          className="flex items-center gap-1 text-sm font-medium text-soundwave-500 hover:text-soundwave-400 transition-colors"
        >
          View All
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        // Skeleton rows shaped like loaded items (48px thumb + 2 text lines,
        // same p-3 + space-y-2 grid) so the card holds its target height while
        // the fetch resolves. Spinner-in-py-12 → 5-row list was a ~300px jump
        // — exactly the "items drawn after the fact" Glenn was seeing.
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl">
              <div className="w-12 h-12 rounded-lg bg-white/5 flex-shrink-0 animate-pulse" />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="h-4 bg-white/5 rounded w-3/4 animate-pulse" />
                <div className="h-3 bg-white/5 rounded w-1/2 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{error}</p>
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-12">
          <Music2 className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: 'var(--text-tertiary)' }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            No files uploaded yet
          </p>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            Upload your first audio file to get started
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {displayedFiles.map((file, index) => {
            // Check if this file was recently claimed
            const isRecentlyClaimed = recentlyClaimedCount > 0 && index < recentlyClaimedCount;
            if (isRecentlyClaimed) {
              console.log(`[RecentFiles] File ${index} (${file.id}) is marked as recently claimed`);
            }

            return (
              <motion.div
                key={file.id}
                onClick={() => handleFileClick(file)}
                className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all group hover:bg-neutral-800/30"
                initial={hasAnimated ? false : { opacity: 0, x: -10 }}
                animate={isRecentlyClaimed
                  ? {
                      opacity: 1,
                      x: 0,
                      backgroundColor: [
                        'rgba(255, 72, 0, 0.15)',
                        'rgba(255, 72, 0, 0.15)',
                        'rgba(255, 72, 0, 0)'
                      ]
                    }
                  : { opacity: 1, x: 0 }
                }
                transition={isRecentlyClaimed
                  ? { duration: 4, times: [0, 0.25, 1], ease: 'easeOut' }
                  : { delay: index * 0.05 }
                }
              >
              {/* Artwork thumbnail if present, else icon (cyan for recordings, slate for uploads).
                  48px matches the RecentCollections row thumb so both dashboard cards render
                  at identical row heights — cards land the same size with the same row count. */}
              {(file.artworkThumbUrl || file.artworkUrl) ? (
                <div className="w-12 h-12 rounded-lg overflow-hidden shadow-md flex-shrink-0">
                  <img
                    src={file.artworkThumbUrl || file.artworkUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-white/5 flex-shrink-0">
                  {file.sourceType === 'recording' ? (
                    <Mic className="w-6 h-6" style={{ color: 'var(--text-tertiary)' }} />
                  ) : (
                    <Music2 className="w-6 h-6" style={{ color: 'var(--text-tertiary)' }} />
                  )}
                </div>
              )}

              <div className="flex-1 min-w-0">
                <p className="font-medium truncate group-hover:text-cyan-500 transition-colors" style={{ color: 'var(--text-primary)' }}>
                  {file.title || file.filename}
                </p>
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(file.uploadedAt)}</span>
                  <span className="hidden sm:inline">•</span>
                  <span className="hidden sm:inline">{formatDuration(file.durationSeconds)}</span>
                  {/* Bitrate deliberately not rendered on this row — it's a
                      file attribute detail that competes with audio-forward
                      framing on the landing. Users who need it can find it
                      on the player page's audio card and on /my-files. */}
                  {/* Retention Period — informational. Permanent files
                      collapse to nothing here (no chip, no leading bullet);
                      the absence carries the "doesn't expire" signal so we
                      don't ship a stray dot at the end of the meta row. */}
                  {!isPermanentFile(file.ttl) && (
                    <>
                      <span>•</span>
                      <FileRetention uploadedAt={file.uploadedAt} ttl={file.ttl} variant="compact" showIcon={false} />
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Copy Link Button */}
                <motion.button
                  onClick={(e) => handleCopyLink(file.id, e)}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Copy link to clipboard"
                >
                  {copiedFileId === file.id ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Clipboard className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                  )}
                </motion.button>

                {/* Delete Button */}
                <motion.button
                  onClick={(e) => handleDeleteFile(file.id, e)}
                  disabled={deletingFileId === file.id}
                  className="p-2 rounded-lg hover:bg-red-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: deletingFileId === file.id ? 1 : 1.1 }}
                  whileTap={{ scale: deletingFileId === file.id ? 1 : 0.9 }}
                  title="Delete file"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </motion.button>

                {/* Only show status badge if NOT completed */}
                {file.processingStatus !== 'completed' && getStatusBadge(file.processingStatus)}
              </div>
            </motion.div>
            );
          })}
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialogComponent />
    </div>
  );
};

export default RecentFiles;
